import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import Delete from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Divider,
  GridLegacy as Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CopyableText } from "components/CopyableText";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { SkeletonFormGroup } from "components/Skeleton";
import { gql } from "generated-graphql";
import {
  CredentialInput,
  CredentialInputForValidation,
  CredentialKind,
  GetAssociatedFacilitiesQuery,
  InventoryOverviewQuery,
  Permission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CredentialPortalPicker } from "routes/Staff/Fulfillment/FulfillmentDetails/CredentialPortalPicker";
import { credentialKindToLabel } from "util/constants";
import { hasCriticalIssues } from "util/forms";

import { CredentialVerification } from "../../Staff/Fulfillment/FulfillmentDetails/CredentialVerification";
import { useCredentialInputValidation } from "../Chemicals/Report/validationHooks/useCredentialInputValidation";
import { EditablePasswordField } from "./EditablePasswordField";
import { GET_CREDENTIAL, UPSERT_CREDENTIAL } from "./schema";
import { SecurityQuestionsField } from "./SecurityQuestionsField";
import { SelectFacilitiesDialog } from "./SelectFacilitiesDialog";

const GET_PORTAL = gql(`
  query GetPortal($id: String!) {
    portal(id: $id) {
      id
      jurisdiction
    }
  }
`);

const GET_ASSOCIATED_FACILITIES = gql(`
  query GetAssociatedFacilities($search: String) {
    facilities(search: $search) {
      items {
        id
        name
        streetAddress1
        city
        state
        zip
        lepc {
          id
          name
        }
      }
    }
  }`);

type AssociatedFacilityRow = NonNullable<
  GetAssociatedFacilitiesQuery["facilities"]["items"]
>[number];

interface CredentialDialogProps {
  onClose: () => void;
  mode?: "add" | "edit";
  credentialId?: string;
  currentFacility?: InventoryOverviewQuery["facility"];
}

export const CredentialDialog: React.FC<CredentialDialogProps> = ({
  onClose,
  mode,
  credentialId,
  currentFacility,
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  const navigate = useNavigate();
  const addingCredential = mode === "add";

  const [selectedPortalJurisdiction, setSelectedPortalJurisdiction] = useState<string | null>(null);

  const [getPortal] = useLazyQuery(GET_PORTAL);

  const { data, loading, previousData } = useQuery(GET_CREDENTIAL, {
    fetchPolicy: "cache-and-network",
    variables: {
      id: credentialId ?? "",
    },
    skip: !credentialId,
  });

  const [upsertCredential, { loading: isUpserting }] = useMutation(UPSERT_CREDENTIAL, {
    refetchQueries: ["Credentials"],
  });

  useEffect(() => {
    if (!loading && !data && !previousData && credentialId) {
      // not-found is a non existent route that will be caught by the wildcard route handler
      // and sent to the NotFound page. See apps/oak/src/main.tsx
      navigate("/not-found");
    }
  }, [loading, data, previousData, credentialId, navigate]);

  const defaultValues: CredentialInputForValidation = useMemo(() => {
    const facilityIds = data?.credential?.facilities?.map((f) => f.id) ?? [];

    if (addingCredential && currentFacility) {
      facilityIds.push(currentFacility.id);
    }
    return {
      id: data?.credential?.id ?? undefined,
      name: data?.credential?.name ?? "",
      username: data?.credential?.username ?? "",
      password: data?.credential?.password ?? "",
      kind: data?.credential?.kind ?? CredentialKind.Tierii,
      email: data?.credential?.email,
      usesCombinedInvoicing: data?.credential?.usesCombinedInvoicing ?? false,
      usesRcoCredential: data?.credential?.usesRcoCredential ?? false,
      securityQuestions: data?.credential?.securityQuestions ?? [],
      isEncampOwned: data?.credential?.isEncampOwned ?? false,
      facilityIds,
      tenantId,
      portalId: data?.credential?.portalId ?? undefined,
    };
  }, [data, currentFacility, addingCredential, tenantId]);

  const form = useValidatingForm<CredentialInputForValidation>(
    defaultValues,
    data?.credential.issues ?? [],
    useCredentialInputValidation(addingCredential)
  );

  const { handleSubmit, control, getValues, setValue, issues, unregister, register, watch } = form;

  const watchedFacilityIds = watch("facilityIds");
  const watchedPortalId = watch("portalId");

  useEffect(() => {
    const fetchPortal = async () => {
      if (watchedPortalId) {
        const { data } = await getPortal({
          variables: { id: watchedPortalId },
        });
        setSelectedPortalJurisdiction(data?.portal?.jurisdiction ?? null);
      }
    };

    fetchPortal();
  }, [watchedPortalId, getPortal]);

  const watchedKind = watch("kind");
  const previousKindRef = useRef(watchedKind);

  // When the user changes kind, clear portalId so they must pick a portal for the new kind.
  useEffect(() => {
    if (!form.formState.isDirty) return;

    if (previousKindRef.current !== watchedKind) {
      previousKindRef.current = watchedKind;
      setValue("portalId", null);
      setSelectedPortalJurisdiction(null);
    }
  }, [watchedKind, setValue, form.formState.isDirty, setSelectedPortalJurisdiction]);

  // We don't need to update this because we disable the ability to copy after
  // the password has been edited. Generally, if you are editing the password
  // you shouldn't (tm) need to copy it.
  const passwordCopyValue = useMemo(() => {
    return data?.credential?.password ?? null;
  }, [data]);

  // Check if user can view credentials
  const canViewCredentials = useMemo(() => {
    return hasPermissions([Permission.ViewCredentials]) || isStaff;
  }, [hasPermissions, isStaff]);

  const [showPasswordCopyIcon, setShowPasswordCopyIcon] = useState(canViewCredentials);

  useEffect(() => {
    if (!canViewCredentials) {
      unregister("password");
    }
  }, [canViewCredentials, unregister]);

  useEffect(() => {
    setShowPasswordCopyIcon(canViewCredentials);
  }, [canViewCredentials]);

  const [openSelectFacilitiesDialog, setOpenSelectFacilitiesDialog] = useState(false);

  const remove = useCallback(
    (index: number) => {
      const newFacilityIds = [...(watchedFacilityIds ?? [])];
      newFacilityIds.splice(index, 1);
      setValue("facilityIds", newFacilityIds, {
        shouldValidate: true,
      });
    },
    [setValue, watchedFacilityIds]
  );

  const onSubmit = async () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    // Create a Set from the array for faster lookup
    const input: CredentialInput = {
      ...getValues(),
      tenantId: tenantId ?? "",
      // This is just to make TS happy. We should not be able to save without these fields
      // because they are required by the CE schema.
      kind: getValues("kind") ?? CredentialKind.Tierii,
      name: getValues("name") ?? "",
      username: getValues("username") ?? "",
    };

    await upsertCredential({
      variables: { id: credentialId, input },
      onCompleted: () => {
        onClose();
        alerts.success("Credential saved successfully");
      },
      onError: (error) => {
        console.error(error);
        alerts.error("An error occurred while saving the Credential");
      },
    });
  };

  // Assumption being made here is that the collection of associated facilities all belong to the same portal jurisdiction.
  // Therefore, we can arbitrarily pick one to use when submitting a verification check.
  const anAssociatedFacility = useMemo(() => {
    if (!data?.credential?.facilities?.length) return;
    return data.credential.facilities[0];
  }, [data?.credential.facilities]);

  const savingOrLoading = loading || isUpserting;
  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="xl"
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: 1,
              paddingRight: 1,
            }}
          >
            {addingCredential ? "Add" : "Edit"} Credential
          </Box>
        }
        primaryActionButtonProps={{
          loading: savingOrLoading,
          onClick: handleSubmit(onSubmit),
          disabled: hasCriticalIssues(issues),
        }}
      >
        {loading && !previousData && (
          <Grid container sx={{ minWidth: 1200 }}>
            <Grid item xs={12} sm={4} paddingRight={2}>
              <SkeletonFormGroup count={4} gridSx={{ p: 0 }} />
            </Grid>
            <Grid item xs={12} sm={8} paddingLeft={2}>
              <Skeleton variant="rounded" sx={{ mb: theme.spacing(1) }} height={350} />
            </Grid>
          </Grid>
        )}

        {(!loading || !!previousData) && (
          <FormProvider {...form}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 0.5,
              }}
            >
              <>
                <Grid container>
                  <Stack
                    direction="row"
                    divider={<Divider orientation="vertical" flexItem />}
                    spacing={theme.spacing(3)}
                  >
                    <Grid item xs={12} sm={4}>
                      <FormTextField
                        name="name"
                        label="Name"
                        control={control}
                        textFieldProps={{ required: true }}
                      />
                      <FormTextField
                        sx={{ mt: 0.5 }}
                        disabled={savingOrLoading}
                        name="username"
                        label={
                          <>
                            {"Username"}
                            {canViewCredentials && (
                              <CopyableText hideText>{getValues("username")}</CopyableText>
                            )}
                          </>
                        }
                        control={control}
                        textFieldProps={{ required: true }}
                      />
                      <EditablePasswordField
                        sx={{ mt: 0.5 }}
                        mode={mode}
                        name="password"
                        label={
                          <>
                            {"Password"}
                            {showPasswordCopyIcon && (
                              <CopyableText hideText>{passwordCopyValue}</CopyableText>
                            )}
                          </>
                        }
                        disabled={savingOrLoading}
                        canViewCredentials={canViewCredentials}
                        onEditClick={() => {
                          // no need to show copy if we're editing the password
                          setShowPasswordCopyIcon(false);
                          register("password");
                          setValue("password", "");
                        }}
                        onCancelEdit={() => {
                          // restore copy functionality and original password value
                          setShowPasswordCopyIcon(canViewCredentials);
                          unregister("password");
                          setValue("password", data?.credential?.password ?? "");
                        }}
                        required
                      />
                      <FormSelect
                        sx={{ mt: 0.5 }}
                        disabled={savingOrLoading}
                        name="kind"
                        label="Type"
                        selectItems={Object.values(CredentialKind).map((kind) => ({
                          display: credentialKindToLabel(kind),
                          value: kind,
                        }))}
                        control={control}
                        rules={{ required: true }}
                      />
                      <FormTextField
                        sx={{ mt: 0.5 }}
                        disabled={savingOrLoading}
                        control={control}
                        name="email"
                        label={
                          <>
                            {"Email"}
                            {canViewCredentials && (
                              <CopyableText hideText>{getValues("email")}</CopyableText>
                            )}
                          </>
                        }
                      />

                      {isStaff && (
                        <CredentialPortalPicker
                          kind={getValues("kind")}
                          control={control}
                          disabled={savingOrLoading}
                        />
                      )}
                      {isStaff && selectedPortalJurisdiction === "WV" && (
                        <CheckboxField
                          control={control}
                          name="usesCombinedInvoicing"
                          label="Uses Combined Invoicing (WV only)"
                        />
                      )}
                      {isStaff && selectedPortalJurisdiction === "AZ" && (
                        <CheckboxField
                          control={control}
                          name="usesRcoCredential"
                          label="RCO Credential (AZ only)"
                        />
                      )}

                      {isStaff && (
                        <CredentialVerification
                          disabled={!data?.credential.facilityCount}
                          credential={data?.credential}
                          facilityId={anAssociatedFacility?.id ?? ""}
                          credentialVerificationAvailable={
                            data?.credential?.portal?.verifiable ?? false
                          }
                        />
                      )}
                      {isStaff && (
                        <Box sx={{ mt: 2 }}>
                          <CheckboxField
                            control={control}
                            name="isEncampOwned"
                            label="Encamp Owned"
                            disabled={savingOrLoading}
                            noErrorDisplay
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", ml: 4 }}
                          >
                            Non-Encamp users will not be able to see this credential
                          </Typography>
                        </Box>
                      )}
                      {isStaff && (
                        <SecurityQuestionsField
                          name="securityQuestions"
                          control={control}
                          disabled={savingOrLoading}
                          canViewCredentials={canViewCredentials}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <AssociatedFacilityGrid
                        facilityIds={watchedFacilityIds ?? []}
                        tenantId={tenantId ?? ""}
                        onRemove={remove}
                        savingOrLoading={savingOrLoading}
                        setOpenSelectFacilitiesDialog={setOpenSelectFacilitiesDialog}
                      />

                      <ErrorDisplay
                        sx={{ mt: 1 }}
                        fields={["facilities"]}
                        errors={form.formState.errors}
                      />
                    </Grid>
                  </Stack>
                </Grid>
              </>
            </Box>
          </FormProvider>
        )}
      </Dialog>
      <SelectFacilitiesDialog
        key={watchedFacilityIds?.join(",") ?? ""}
        open={openSelectFacilitiesDialog}
        onCancel={() => setOpenSelectFacilitiesDialog(false)}
        onSave={(selectedFacilityIds) => {
          setValue("facilityIds", selectedFacilityIds, {
            shouldValidate: true,
          });
          setOpenSelectFacilitiesDialog(false);
        }}
        initialSelectedIds={watchedFacilityIds ?? []}
        portalJurisdiction={selectedPortalJurisdiction}
      />
    </>
  );
};

type FacilityGridProps = {
  facilityIds: string[];
  onRemove: (indexToRemove: number) => void;
  tenantId: string;
  savingOrLoading: boolean;
  setOpenSelectFacilitiesDialog: (open: boolean) => void;
};

const AssociatedFacilityGrid = ({
  facilityIds,
  onRemove,
  tenantId,
  savingOrLoading,
  setOpenSelectFacilitiesDialog,
}: FacilityGridProps) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // copy the facilityIds prop so we can control when it is updated. otherwise,
  // every time the prop changes (when we delete facilities for example)
  // we will re run this query and cause an issue with not properly updating
  // the associated facilities DataGrid
  const [prevFacilityIdsLength, setPrevFacilityIdsLength] = useState(0);

  const [loadFacilities, { data, loading }] = useLazyQuery(GET_ASSOCIATED_FACILITIES);

  const facilities = useMemo(
    () => (data?.facilities?.items ?? []).filter((f) => facilityIds.includes(f.id)),
    [data?.facilities?.items, facilityIds]
  );

  useEffect(() => {
    // if the number of facility ids has increased, we have added facilities
    // to our association list and need to fetch the new ones to show in this
    // datagrid.
    if (facilityIds.length > prevFacilityIdsLength) {
      (async () =>
        await loadFacilities({
          variables: {
            search: `tenantId:${tenantId} ids:${(facilityIds ?? []).join(",")}`,
          },
        }))();
    }

    setPrevFacilityIdsLength(facilityIds.length);
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [prevFacilityIdsLength, facilityIds, loadFacilities]);

  const columns: GridColDef<AssociatedFacilityRow>[] = [
    { field: "name", headerName: "Facility", flex: 1 },
    {
      field: "streetAddress1",
      headerName: "Street Address",
      flex: 1,
      valueGetter(_, row) {
        if (!row.streetAddress1) return "";

        const { streetAddress1, city, state, zip } = row;

        return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
      },
    },
    {
      field: "state",
      headerName: "State",
      flex: 0.2,
    },
    {
      field: "lepc.name",
      headerName: "LEPC",
      flex: 0.5,
      valueGetter: (_, row) => row.lepc?.name,
      renderCell: ({ value }) => {
        return (
          <Tooltip title={value}>
            <Typography variant="body2">{value}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.1,
      getActions: (params) => {
        const index = facilityIds.findIndex((f) => f === params.row.id);
        return [
          <Tooltip title="Delete facility association" key={1}>
            <GridActionsCellItem label="Delete" onClick={() => onRemove(index)} icon={<Delete />} />
          </Tooltip>,
        ];
      },
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Associated Facilities</Typography>
        <Button
          disabled={savingOrLoading}
          size="small"
          startIcon={<AddCircleOutlineRounded />}
          variant="text"
          onClick={() => setOpenSelectFacilitiesDialog(true)}
        >
          Link Facilities
        </Button>
      </Stack>
      <Box>
        <DataGrid
          loading={loading}
          rows={facilities}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="client"
        />
      </Box>
    </>
  );
};
